// src/stores/alertPreferencesStore.js
import { defineStore } from 'pinia'
import { doc, getDoc, setDoc, runTransaction, getDocFromServer } from 'firebase/firestore'
import { deleteField } from 'firebase/firestore'
import { onSnapshot } from '@/services/sessionListeners'
import { isEqual } from 'lodash-es'
import { logEvent } from '@/utils/firebase/eventLogger'
import { db } from '@/services/firebaseconfig'
import { useUserStore } from './userStore'

import preferenceCatalog from '../../functions-shared/notifications/alertPreferenceCatalog'
import preferenceModel from '../../functions-shared/notifications/alertPreferenceModel'
const {
  DEFAULT_CHANNELS,
  DEFAULT_CONSOLIDATION,
  DEFAULT_EVENTS,
  EVENT_METADATA,
  CHANNEL_NOT_APPLICABLE,
  SCHEMA_VERSION,
  eventVisible,
} = preferenceCatalog
export { EVENT_METADATA, CHANNEL_NOT_APPLICABLE }

export const useAlertPreferencesStore = defineStore('alertPreferences', {
  state: () => ({
    channels: { ...DEFAULT_CHANNELS },
    events: JSON.parse(JSON.stringify(DEFAULT_EVENTS)),
    consolidation: JSON.parse(JSON.stringify(DEFAULT_CONSOLIDATION)),
    // What the saved document holds: only the person's own choices (the catalog supplies the rest when it is read), and
    // whether it is current-format. channels/events/consolidation above are what Settings shows.
    saved: { channels: {}, events: {}, consolidation: {} },
    savedCurrent: false,
    globalOverrides: null, // Loaded from globalSettings/alertOverrides
    overridesLoaded: false,
    overridesError: null,
    isLoading: false,
    hasLoaded: false,
    error: null,
    _unsub: null,
    _unsubOverrides: null,
  }),

  getters: {
    /**
     * Check if a channel is enabled globally
     */
    isChannelEnabled: state => channel => preferenceModel.channelEnabled(state, channel),

    /**
     * Check if a global channel is locked by admin
     */
    isChannelLocked: state => channel => preferenceModel.channelLocked(state, channel),

    /**
     * Check if a specific channel is enabled for an event
     * Takes into account: global channel setting, event setting, and forced overrides
     */
    isEventChannelEnabled: state => (eventKind, channel) =>
      preferenceModel.eventEnabled(state, eventKind, channel),

    /**
     * Check if an event/channel combo is forced (locked ON) by admin
     */
    isForced: state => (eventKind, channel) => {
      if (channel === 'sms') return false
      if (eventKind === 'buildPartnerJobArrived' && state.events[eventKind]?.[channel] !== true)
        return false
      return state.globalOverrides?.forcedEvents?.[eventKind]?.[channel] === true
    },

    /**
     * Check if an event/channel combo is locked OFF by admin
     */
    isLocked: state => (eventKind, channel) => {
      return state.globalOverrides?.lockedEvents?.[eventKind]?.[channel] === true
    },

    /**
     * Check if an event is globally disabled by admin
     */
    isEventDisabled: state => eventKind => {
      return state.globalOverrides?.disabledEvents?.[eventKind] === true
    },

    /**
     * Get consolidation settings for an event type
     */
    getConsolidation: state => eventKind => {
      return (
        state.consolidation[eventKind] ||
        DEFAULT_CONSOLIDATION[eventKind] || { enabled: false, after: 0 }
      )
    },

    /**
     * Get all events grouped by category for UI
     */
    eventsByCategory: () => {
      const grouped = {}
      for (const [eventKind, meta] of Object.entries(EVENT_METADATA)) {
        if (!eventVisible(eventKind, useUserStore().isSuperAdmin)) continue
        if (!grouped[meta.category]) {
          grouped[meta.category] = []
        }
        grouped[meta.category].push({
          kind: eventKind,
          ...meta,
        })
      }
      return grouped
    },
  },

  actions: {
    /**
     * Load preferences from Firestore
     */
    async load() {
      const userStore = useUserStore()
      const uid = userStore.user?.uid
      if (!uid) return

      if (this.isLoading) return
      this.isLoading = true
      this.error = null

      try {
        // Load user preferences
        const prefsRef = doc(db, `users/${uid}/alertPreferences/default`)
        const snap = await getDoc(prefsRef)
        const data = snap.exists() ? snap.data() : null
        // Only a document read as the old format (or none) needs the old notificationPrefs. Loading writes nothing:
        // the catalog supplies every default when it is read.
        let old = {}
        if (!(data?._version >= 2)) {
          const oldSnap = await getDoc(doc(db, `users/${uid}/notificationPrefs/default`))
          old = oldSnap.exists() ? oldSnap.data() : {}
        }
        this.adoptPreferences(data, old)

        this.hasLoaded = true

        // Start listening to global overrides
        this.subscribeToOverrides()
      } catch (err) {
        console.error('[alertPreferencesStore] load error:', err)
        this.error = err
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Subscribe to real-time updates for global overrides
     */
    subscribeToOverrides() {
      if (this._unsubOverrides) return

      const overridesRef = doc(db, 'globalSettings/alertOverrides')
      this._unsubOverrides = onSnapshot(
        overridesRef,
        { includeMetadataChanges: true },
        snap => {
          if (snap.exists()) {
            this.globalOverrides = snap.data()
          } else {
            this.globalOverrides = null
          }
          this.overridesLoaded = !snap.metadata.fromCache && !snap.metadata.hasPendingWrites
          this.overridesError = null
        },
        err => {
          this.overridesLoaded = false
          this.overridesError = err
          console.error('[alertPreferencesStore] overrides subscription error:', err)
        }
      )
    },

    async saveGlobalOverrides(overrides, baseline) {
      if (!this.overridesLoaded || this.overridesError) {
        throw new Error('Wait for admin overrides to finish loading before saving.')
      }
      const ref = doc(db, 'globalSettings/alertOverrides')
      await runTransaction(db, async transaction => {
        const snapshot = await transaction.get(ref)
        if (!isEqual(snapshot.exists() ? snapshot.data() : null, baseline)) {
          throw new Error(
            'Admin overrides changed while you were editing. Reopen preferences before saving.'
          )
        }
        // Replace each owned map, so clearing overrides works without deleting other fields.
        transaction.set(ref, overrides, { mergeFields: Object.keys(overrides) })
      })
      await logEvent({
        eventType: 'SYSTEM_CONFIG_UPDATED',
        action: 'update',
        resource: { type: 'globalSettings', id: 'alertOverrides' },
        changes: { before: baseline, after: overrides, fields: Object.keys(overrides) },
        context: { source: 'frontend', metadata: { section: 'alertOverrides' } },
      })
      return (await getDocFromServer(ref)).data()
    },

    /**
     * Merge loaded events with defaults (to pick up new event types)
     */
    mergeEvents(loadedEvents) {
      return preferenceModel.mergeEvents(loadedEvents)
    },

    /**
     * Show a saved document as Settings reads it (alertPreferenceModel.loadPreferences: the person's choices over the
     * catalog's defaults, or the old notificationPrefs format) and keep the choices themselves for the next write.
     */
    adoptPreferences(existing, old = {}) {
      const shown = preferenceModel.loadPreferences(existing, old)
      this.channels = shown.channels
      this.events = shown.events
      this.consolidation = shown.consolidation
      this.saved = preferenceModel.savedChoices(existing, old)
      this.savedCurrent = existing?._version >= 2
    },

    /**
     * Save one choice, and only that: the saved document holds the person's choices, never copies of the defaults, so a
     * default the catalog changes later reaches everyone who has not chosen (docs/bugs/alert_defaults_two_lists_BUG.md).
     * The first write over no document, or one read as the old format, starts a current-format one from its choices.
     */
    async writeChoice(choice = {}) {
      const uid = useUserStore().user?.uid
      if (!uid) return
      // A write starts from the loaded document: before the load, "no document" is not known to be true.
      if (!this.hasLoaded) await this.load()
      if (!this.hasLoaded) throw new Error('Alert preferences are still loading. Try again in a moment.')
      const prefsRef = doc(db, `users/${uid}/alertPreferences/default`)
      const fresh = !this.savedCurrent
      const body = fresh ? preferenceModel.mergeChoices(this.saved, choice) : choice
      await setDoc(
        prefsRef,
        { ...body, _version: SCHEMA_VERSION, _updatedAt: new Date() },
        fresh ? {} : { merge: true }
      )
      this.saved = preferenceModel.mergeChoices(this.saved, choice)
      this.savedCurrent = true
    },

    /**
     * Update a global channel setting
     */
    async setChannel(channel, enabled) {
      // Can't modify locked channels
      if (this.isChannelLocked(channel)) return
      const before = this.channels[channel]
      this.channels[channel] = enabled
      await this.writeChoice({ channels: { [channel]: enabled } })
      await this.logPreferenceChange(`channels.${channel}`, before, enabled)
    },

    /**
     * Update an event-specific channel setting
     */
    async setEventChannel(eventKind, channel, enabled) {
      // Can't modify forced/locked settings
      if (this.isForced(eventKind, channel) || this.isLocked(eventKind, channel)) return

      // Can't modify N/A settings
      if (CHANNEL_NOT_APPLICABLE[eventKind]?.includes(channel)) return

      if (!this.events[eventKind]) {
        this.events[eventKind] = { ...(DEFAULT_EVENTS[eventKind] || {}) }
      }
      const before = this.events[eventKind][channel]
      this.events[eventKind][channel] = enabled
      await this.writeChoice({ events: { [eventKind]: { [channel]: enabled } } })
      await this.logPreferenceChange(`events.${eventKind}.${channel}`, before, enabled)
    },

    async logPreferenceChange(field, before, after) {
      const uid = useUserStore().user?.uid
      if (!uid) return
      await logEvent({
        eventType: 'ALERT_PREFERENCES_UPDATED',
        action: 'update',
        resource: { type: 'user', id: uid },
        changes: {
          before: { [field]: before ?? null },
          after: { [field]: after },
          fields: [field],
        },
      })
    },

    /**
     * Update consolidation setting for an event type
     */
    async setConsolidation(eventKind, { enabled, after }) {
      if (!this.consolidation[eventKind]) {
        this.consolidation[eventKind] = {
          ...(DEFAULT_CONSOLIDATION[eventKind] || { enabled: false, after: 3 }),
        }
      }
      const before = { ...this.consolidation[eventKind] }
      if (typeof enabled === 'boolean') {
        this.consolidation[eventKind].enabled = enabled
      }
      if (typeof after === 'number') {
        this.consolidation[eventKind].after = after
      }
      // A kind's consolidation is read as one row (enabled and after together), so the touched kind's row is the choice.
      const row = { ...this.consolidation[eventKind] }
      await this.writeChoice({ consolidation: { [eventKind]: row } })
      await this.logPreferenceChange(`consolidation.${eventKind}`, before, row)
    },

    /**
     * Reset all preferences to defaults
     */
    async resetToDefaults() {
      const uid = useUserStore().user?.uid
      if (!uid) return
      // Every choice removed; the catalog's defaults then apply. The document's other fields are kept.
      await setDoc(
        doc(db, `users/${uid}/alertPreferences/default`),
        {
          channels: deleteField(),
          events: deleteField(),
          consolidation: deleteField(),
          _version: SCHEMA_VERSION,
          _updatedAt: new Date(),
        },
        { merge: true }
      )
      const before = this.saved
      this.adoptPreferences({ _version: SCHEMA_VERSION }, {})
      await this.logPreferenceChange('all', before, 'defaults')
    },

    /**
     * Cleanup subscriptions
     */
    cleanup() {
      this.overridesLoaded = false
      this.overridesError = null
      this.globalOverrides = null
      if (this._unsub) {
        this._unsub()
        this._unsub = null
      }
      if (this._unsubOverrides) {
        this._unsubOverrides()
        this._unsubOverrides = null
      }
    },
  },
})
